import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useEntities } from "@/lib/queries";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles, StickyNote, Superscript, Flame, Share2, Info } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { ChapterInfo } from "@/components/studio/ChapterInfo";
import { ShareQuoteDialog } from "@/components/quotes/ShareQuoteDialog";
import { useAuth } from "@/context/AuthContext";
import { useLiveCheck, IssuePopover, liveCheckPref, setLiveCheckPref } from "@/components/studio/liveCheck";
import { Switch } from "@/components/ui/switch";
import { ReviewSheet } from "@/components/studio/ReviewSheet";
import { AnnotationDialog, insertAnnotation } from "@/components/studio/Annotations";
import { useMusic } from "@/lib/music/MusicProvider";
import { isPaid } from "@/components/music/MusicWidget";
import { WidgetPanel } from "@/components/studio/widgets/WidgetPanel";
import { PoetryMarks, refreshPoetry } from "@/components/poetry/PoetryMarks";
import { PoetryContext, ModeSwitch, usePoetryDict } from "@/components/poetry/PoetryWidget";
import { FORMS } from "@/lib/poetry/forms";
import { ScriptMarks, refreshScript } from "@/components/script/ScriptMarks";
import { ScriptBar } from "@/components/script/ScriptBar";
import { isScript } from "@/lib/script";
import { Confetti, computeStreak, streakParams } from "@/components/studio/streaks";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Save, Lock, EyeOff, Eye, PanelRightDashed, PanelTop } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DesktopOnly } from "@/components/studio/DesktopOnly";
import { PageEditor } from "@/components/studio/PageEditor";
import { MobileChapter } from "@/components/studio/mobile/MobileChapter";
import { useIsMobile } from "@/lib/useIsMobile";
import { api, errMsg } from "@/lib/api";
import { draft, merge3 } from "@/lib/draftStore";
import { countWords, fmtNum, fmtMinutes, readMinutes, localDay, PAGE_SIZES } from "@/lib/studio";

const SaveState = ({ state }) => <span className="text-xs text-[#A8A090]" data-testid="chapter-save-state">{{ saved: "All changes saved", dirty: "Unsaved changes", saving: "Saving…", error: "Couldn't save", offline: "Offline · saved on device" }[state]}</span>;

const restoreChapter = (cid, ch) => {
  const d = draft.get(`ch:${cid}`);
  if (!d?.patch) return null;
  const patch = { ...d.patch };
  if (patch.content !== undefined) patch.content = merge3(d.base ?? ch.content, patch.content, ch.content);
  if (patch.title !== undefined) patch.title = merge3(d.baseTitle ?? ch.title, patch.title, ch.title);
  Object.keys(patch).forEach((k) => { if (patch[k] === ch[k]) delete patch[k]; });
  if (!Object.keys(patch).length) { draft.clear(`ch:${cid}`); return null; }
  return patch;
};

const ChapterNav = ({ chapters, cid, link }) => {
  const i = chapters.findIndex((c) => c.id === cid);
  const prev = chapters[i - 1], next = chapters[i + 1];
  return (
    <div className="flex gap-1">
      <Button size="sm" variant="ghost" disabled={!prev} asChild={!!prev}>{prev ? <Link to={link(prev)} className="inline-flex items-center gap-1 whitespace-nowrap" data-testid="chapter-prev"><ChevronLeft className="h-4 w-4" />Previous</Link> : <span className="inline-flex items-center gap-1 whitespace-nowrap"><ChevronLeft className="h-4 w-4" />Previous</span>}</Button>
      <Button size="sm" variant="ghost" disabled={!next} asChild={!!next}>{next ? <Link to={link(next)} className="inline-flex items-center gap-1 whitespace-nowrap" data-testid="chapter-next">Next<ChevronRight className="h-4 w-4" /></Link> : <span className="inline-flex items-center gap-1 whitespace-nowrap">Next<ChevronRight className="h-4 w-4" /></span>}</Button>
    </div>
  );
};

const sceneText = (ed) => {
  if (!ed) return "";
  const { doc, selection } = ed.state;
  const end = selection.from > 1 ? selection.from : doc.content.size;
  return doc.textBetween(Math.max(0, end - 3000), end, "\n", " ").trim();
};

// Separate component so music state changes don't re-render (and remount) the chapter page.
const StorySoundtrack = ({ sid, cid }) => {
  const music = useMusic();
  const { user } = useAuth();
  const paid = isPaid(user);
  useEffect(() => { if (paid) music?.storyOpened(sid); }, [sid, cid, paid, music?.status, music?.listsReady]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

export default function ChapterPage() {
  const { base } = useOutletContext();
  const { sid, cid } = useParams();
  const { data: ch, error } = useQuery({ queryKey: ["chapter", cid], queryFn: () => api.get(`/items/${cid}`).then((r) => r.data), retry: false, refetchOnWindowFocus: false, staleTime: Infinity });
  if (error) return <p className="p-10 text-sm text-[#F87171]" data-testid="chapter-error">{errMsg(error)}</p>;
  if (!ch) return null;
  return <><StorySoundtrack sid={sid} cid={cid} /><ChapterWorkspace key={cid} ch={ch} base={base} sid={sid} cid={cid} /></>;
}

const todayParams = () => { const s = new Date(); s.setHours(0, 0, 0, 0); return { start: s.toISOString().slice(0, 13), end: new Date().toISOString().slice(0, 13) }; };

const useDailyGoal = (sid, goal, enabled) => {
  const { data: today } = useQuery({ queryKey: ["story-stats-today", sid], queryFn: () => api.get(`/stories/${sid}/stats`, { params: todayParams() }).then((x) => x.data), enabled });
  const { data: long } = useQuery({ queryKey: ["story-stats", sid, "streak"], queryFn: () => api.get(`/stories/${sid}/stats`, { params: streakParams() }).then((x) => x.data), enabled: enabled && goal > 0 });
  const words = (today?.hours || []).reduce((a, h) => a + h.words, 0);
  const first = useRef(null);
  const [party, setParty] = useState(false);
  useEffect(() => {
    if (!goal || !today) return undefined;
    if (first.current === null) { first.current = words; return undefined; }
    const key = `lf-goal-${sid}-${localDay(new Date())}`;
    if (first.current < goal && words >= goal && !localStorage.getItem(key)) {
      localStorage.setItem(key, "1");
      setParty(true);
      toast.success(`Goal reached! ${fmtNum(words)} words today.`);
      const t = setTimeout(() => setParty(false), 3500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [words, goal, today, sid]);
  return { words, party, streak: computeStreak(long?.hours, goal) };
};

function ChapterWorkspace({ ch, base, sid, cid }) {
  const qc = useQueryClient();
  const [restored] = useState(() => (ch.role === "owner" && !ch.locked ? restoreChapter(cid, ch) : null));
  const [title, setTitle] = useState(restored?.title ?? ch.title);
  const [content, setContent] = useState(restored?.content ?? ch.content);
  const [hidden, setHidden] = useState(!!ch.hidden);
  const [state, setState] = useState(restored ? "dirty" : "saved");
  const baseRef = useRef({ content: ch.content, title: ch.title });
  const retryT = useRef(null);
  const tries = useRef(0);
  const [pages, setPages] = useState(1);
  const [size, setSize] = useState(ch.story.page_size || "letter");
  const pending = useRef(restored);
  const [headCollapsed, setHeadCollapsed] = useState(() => localStorage.getItem("lf_chapter_head_collapsed") === "1");
  const [inPanel, setInPanel] = useState(false);
  const panelApi = useRef(null);
  const headRef = useRef(null);
  const [headH, setHeadH] = useState(0);
  useEffect(() => {
    const el = headRef.current;
    if (!el) { setHeadH(0); return undefined; }
    const ro = new ResizeObserver(() => setHeadH(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, [inPanel]);
  const editable = ch.role === "owner" && !ch.locked;
  const mobile = useIsMobile();
  const [mode, setModeRaw] = useState(ch.mode || "prose");
  const [form, setFormRaw] = useState(ch.form && FORMS[ch.form] ? ch.form : ch.mode === "song" ? "song" : "free");
  const [poetryExt] = useState(() => PoetryMarks.configure({ mode: ch.mode || "prose", form: ch.form || (ch.mode === "song" ? "song" : "free") }));
  const [scriptExt] = useState(() => ScriptMarks.configure({ mode: ch.mode || "prose" }));
  const nav = useNavigate();
  const [editorObj, setEditorObj] = useState(null);
  useEffect(() => { if (editorObj && isScript(mode)) refreshScript(editorObj, { mode }); }, [editorObj]); // eslint-disable-line react-hooks/exhaustive-deps
  const { user } = useAuth();
  const [live, setLive] = useState(() => liveCheckPref(user?.id));
  const { data: lore } = useEntities(ch.story.universe_id);
  const loreNames = useMemo(() => (lore ? new Set(lore.flatMap((e) => [e.name, ...(e.aliases || [])]).flatMap((n) => [n, ...n.split(/\s+/)]).map((n) => n.toLowerCase())) : undefined), [lore]);
  const lc = useLiveCheck({ cid, enabled: live && editable && !mobile, names: loreNames });
  const onEditor = useCallback((ed) => { setEditorObj(ed); lc.setEditor(ed); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleLive = (on) => { setLive(on); setLiveCheckPref(user?.id, on); };
  usePoetryDict(mode === "poem" || mode === "song", editorObj);
  const setMode = (m) => {
    const f = m === "song" ? "song" : form === "song" ? "free" : form;
    setModeRaw(m); setFormRaw(f); queue({ mode: m, form: f }); refreshPoetry(editorObj, { mode: m, form: f }); refreshScript(editorObj, { mode: m });
    if (isScript(m)) toast.success(`${m === "stage" ? "Stage play" : "Screenplay"} mode: pick each line's type in the bar above the page (Tab cycles)`);
    else if (m !== "prose") { panelApi.current?.addPoetry(); toast.success(`${m === "song" ? "Song" : "Poem"} mode: syllable counts and rhyme letters appear beside each line`); }
  };
  const setForm = (f) => { setFormRaw(f); queue({ form: f }); refreshPoetry(editorObj, { form: f }); };
  const [reviewOpen, setReviewOpen] = useState(false);
  const [info, setInfoState] = useState(() => restored?.info ?? ch.info ?? {});
  const infoRef = useRef(info);
  const [infoOpen, setInfoOpen] = useState(false);
  const [fixes, setFixes] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [annot, setAnnot] = useState(null);
  const [shareText, setShareText] = useState(null);
  const contentRef = useRef(content);
  contentRef.current = content;
  const goal = ch.role === "owner" ? ch.story.goals?.daily_words || 0 : 0;
  const daily = useDailyGoal(sid, goal, ch.role === "owner");
  const commercial = ch.tier === "commercial";
  const save = useCallback(async () => {
    const body = pending.current;
    if (!body) return;
    pending.current = null;
    setState("saving");
    clearTimeout(retryT.current);
    try {
      await api.patch(`/items/${cid}`, body);
      tries.current = 0;
      baseRef.current = { content: body.content ?? baseRef.current.content, title: body.title ?? baseRef.current.title };
      if (!pending.current) draft.clear(`ch:${cid}`);
      setState(pending.current ? "dirty" : "saved"); qc.invalidateQueries({ queryKey: ["draft-items"] }); qc.removeQueries({ queryKey: ["chapter", cid] }); qc.invalidateQueries({ queryKey: ["story-stats"] }); qc.invalidateQueries({ queryKey: ["story-stats-today"] }); qc.invalidateQueries({ queryKey: ["writing-stats"] }); }
    catch (e) {
      pending.current = { ...body, ...(pending.current || {}) };
      if (!e.response || e.response.status >= 500) {
        setState("offline");
        retryT.current = setTimeout(() => saveRef.current(), Math.min(30000, 2000 * 2 ** tries.current++));
      } else { setState("error"); toast.error(errMsg(e)); }
    }
  }, [cid, qc]);
  const saveRef = useRef(save);
  saveRef.current = save;
  const queue = (patch) => {
    pending.current = { ...(pending.current || {}), ...patch };
    draft.set(`ch:${cid}`, { patch: pending.current, base: baseRef.current.content, baseTitle: baseRef.current.title });
    setState((st) => (st === "offline" ? st : "dirty"));
  };
  useEffect(() => {
    if (restored) toast.info("Restored unsaved changes from this device", { id: `restore-${cid}` });
    const wake = () => { if (pending.current && document.visibilityState === "visible") { clearTimeout(retryT.current); saveRef.current(); } };
    window.addEventListener("online", wake);
    document.addEventListener("visibilitychange", wake);
    return () => { clearTimeout(retryT.current); window.removeEventListener("online", wake); document.removeEventListener("visibilitychange", wake); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (state !== "dirty") return undefined; const t = setTimeout(save, 1500); return () => clearTimeout(t); }, [state, content, title, save]);
  useEffect(() => () => { if (pending.current) api.patch(`/items/${cid}`, pending.current).catch(() => {}); }, [cid]);
  const onPages = useCallback((n) => setPages(n), []);
  const setPageSize = async (s) => { setSize(s); await api.patch(`/stories/${sid}`, { page_size: s }); };
  const applyFix = (f) => { const next = contentRef.current.replace(f.original, f.suggestion); contentRef.current = next; setContent(next); queue({ content: next }); };
  const saveAndReview = async () => {
    await save();
    setFixes([]);
    setReviewOpen(true);
    api.get(`/items/${cid}/suggestions`).then((r) => setSuggestions(r.data));
  };
  const tools = editable && (<>
    {isScript(mode) && <ScriptBar editor={editorObj} mode={mode} />}
    <div className="flex items-center gap-1 border-t border-[#2D3442] px-2 py-1" data-testid="annotation-tools">
      <label className="mr-2 flex items-center gap-2 text-xs text-[#A8A090]" title="Free spelling and grammar check that runs on this device as you write"><Switch checked={live} onCheckedChange={toggleLive} data-testid="live-check-toggle" />Live check</label>
      {commercial && <><span className="mr-1 text-[10px] uppercase tracking-[0.16em] text-[#A8A090]">Commercial</span>
      <Button size="sm" variant="ghost" className="h-7" onClick={() => setAnnot("footnote")} data-testid="add-footnote"><Superscript className="mr-1 h-3.5 w-3.5" />Footnote</Button>
      <Button size="sm" variant="ghost" className="h-7" onClick={() => setAnnot("note")} data-testid="add-note-box"><StickyNote className="mr-1 h-3.5 w-3.5" />Note box</Button></>}
    </div>
  </>);
  const link = (c) => `${base}/studio/${sid}/c/${c.id}`;
  const shareSel = (ed) => {
    const { from, to } = ed.state.selection;
    const text = ed.state.doc.textBetween(from, to, " ").replace(/\s+/g, " ").trim();
    if (!text) return;
    if (text.length > 600) { toast.error("Pick a shorter passage (600 characters max) for a card"); return; }
    setShareText(text);
  };
  const bubble = ch.role === "owner" && ((ed) => (shareText ? null :
    <button onMouseDown={(e) => e.preventDefault()} onClick={() => shareSel(ed)} className="flex items-center gap-1 rounded-md border border-[#2D3442] bg-[#0F131A] px-2.5 py-1.5 text-xs text-[#F3EBDD] shadow-lg hover:border-[#D97706] hover:text-[#F59E0B]" data-testid="chapter-share-selection"><Share2 className="h-3.5 w-3.5" />Share as card</button>
  ));
  const words = countWords(content);
  const chapterNo = ch.chapters.findIndex((c) => c.id === cid) + 1;
  const backLink = <Link to={`${base}/studio/${sid}`} className="inline-flex min-w-0 max-w-full items-center gap-1 text-xs text-[#A8A090] hover:text-[#F3EBDD]" data-testid="back-to-story"><ChevronLeft className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{ch.story.title} · {ch.draft.name}</span></Link>;
  const numberEl = <span className="whitespace-nowrap font-display text-3xl text-[#F59E0B]" data-testid="chapter-number">Chapter {chapterNo}</span>;
  const titleEl = (
    <>
      {!editable && title && <span className="font-display text-3xl text-[#F3EBDD] [overflow-wrap:anywhere]" data-testid="chapter-title-text">· {title}</span>}
      {editable && <Input value={title} disabled={!editable} maxLength={200} placeholder="Chapter name (optional)" onChange={(e) => { setTitle(e.target.value); queue({ title: e.target.value }); }} className="h-11 max-w-md border-[#2D3442] bg-[#0F131A] font-display text-2xl md:text-2xl text-[#F3EBDD]" data-testid="chapter-title-input" />}
    </>
  );
  const statsEls = (
    <>
      <ModeSwitch mode={mode} setMode={setMode} disabled={!editable} />
      <span data-testid="chapter-word-count"><b className="text-[#F3EBDD]">{fmtNum(words)}</b> words</span>
      {isScript(mode) ? <span data-testid="chapter-runtime" title="About one minute per page">~<b className="text-[#F3EBDD]">{pages}</b> min {mode === "stage" ? "on stage" : "screen time"}</span>
        : <span data-testid="chapter-read-time">~<b className="text-[#F3EBDD]">{fmtMinutes(readMinutes(words, ch.story.goals?.wpm))}</b> read</span>}
      {ch.role === "owner" && ch.story.goals?.chapter_words > 0 && <span data-testid="chapter-goal">{Math.min(100, Math.round((words / ch.story.goals.chapter_words) * 100))}% of {fmtNum(ch.story.goals.chapter_words)}</span>}
      <span data-testid="chapter-page-count"><b className="text-[#F3EBDD]">{pages}</b> page{pages === 1 ? "" : "s"}</span>
      <div className="flex overflow-hidden rounded border border-[#2D3442]">
        {Object.entries(PAGE_SIZES).map(([k, v]) => <button key={k} disabled={!editable} onClick={() => setPageSize(k)} className={`px-2.5 py-1 text-xs ${size === k ? "bg-[#D97706] text-[#0B0D10]" : "hover:bg-[#1A202C]"}`} data-testid={`page-size-${k}`}>{v.label}</button>)}
      </div>
      {editable && <button onClick={() => { queue({ hidden: !hidden }); setHidden(!hidden); }} className="flex items-center gap-1 text-xs hover:text-[#F3EBDD]" data-testid="chapter-hidden-toggle">{hidden ? <><EyeOff className="h-3.5 w-3.5" />Hidden from team</> : <><Eye className="h-3.5 w-3.5" />Visible with story</>}</button>}
      {goal > 0 && <span className="flex items-center gap-1" data-testid="chapter-streak"><Flame className={`h-4 w-4 ${daily.streak ? "text-[#F59E0B]" : ""}`} /><b className="text-[#F3EBDD]">{daily.streak}</b>-day streak · <span data-testid="chapter-today-words">{fmtNum(daily.words)}/{fmtNum(goal)} today</span></span>}
      <button onClick={() => setInfoOpen(true)} className="flex items-center gap-1 text-xs hover:text-[#F3EBDD]" data-testid="chapter-info-button"><Info className="h-3.5 w-3.5" />Chapter info</button>
    </>
  );
  const actionsEl = editable && (
    <>
      <SaveState state={state} />
      <Button size="sm" variant="outline" onClick={save} disabled={state === "saved" || state === "saving"} data-testid="chapter-save-button"><Save className="mr-1 h-4 w-4" />Save</Button>
      <Button size="sm" onClick={saveAndReview} data-testid="chapter-review-button"><Sparkles className="mr-1 h-4 w-4" />Save & lore check</Button>
    </>
  );
  const toggleHead = () => setHeadCollapsed((v) => { localStorage.setItem("lf_chapter_head_collapsed", v ? "0" : "1"); return !v; });
  const setInfo = (patch) => {
    const next = { ...infoRef.current, ...patch };
    infoRef.current = next; setInfoState(next);
    if (next.year_from == null || next.year_to == null || next.year_to >= next.year_from) queue({ info: next });
  };
  const infoEl = <ChapterInfo uid={ch.story.universe_id} cid={cid} info={info} onChange={setInfo} editable={editable} onSendIdea={(t) => nav(`${base}/notes`, { state: { quickNote: t } })} />;
  const infoBtn = <button onClick={() => setInfoOpen(true)} className="flex items-center gap-1 text-xs hover:text-[#F3EBDD]" data-testid="chapter-info-button"><Info className="h-3.5 w-3.5" />Chapter info</button>;
  const infoSheet = (
    <Sheet open={infoOpen} onOpenChange={setInfoOpen}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-md" data-testid="chapter-info-sheet">
        <SheetHeader className="mb-4"><SheetTitle className="font-display text-2xl">Chapter info</SheetTitle><SheetDescription>Who, where and when, for your own reference and the free lore check.</SheetDescription></SheetHeader>
        {infoEl}
      </SheetContent>
    </Sheet>
  );
  if (mobile) return (
    <MobileChapter ch={ch} cid={cid} sid={sid} base={base} chapterNo={chapterNo} title={title} onTitle={(t) => { setTitle(t); queue({ title: t }); }} editable={editable} state={state}
      mode={mode} setMode={setMode} words={words} content={content} onContent={(md) => { if (md === content) return; setContent(md); queue({ content: md }); }} onEditor={onEditor} editorObj={editorObj} extensions={[poetryExt, scriptExt]} infoBtn={infoBtn} infoSheet={infoSheet} />
  );
  const headControls = (
    <span className="flex items-center gap-0.5">
      <button onClick={() => panelApi.current?.addChapter()} title="Move to side panel" className="rounded p-1 text-[#A8A090] hover:bg-[#1A202C] hover:text-[#F3EBDD]" data-testid="chapter-header-to-panel"><PanelRightDashed className="h-4 w-4" /></button>
      <button onClick={toggleHead} title={headCollapsed ? "Expand header" : "Collapse header"} className="rounded p-1 text-[#A8A090] hover:bg-[#1A202C] hover:text-[#F3EBDD]" data-testid="chapter-header-collapse">{headCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}</button>
    </span>
  );
  const chapterTools = (
    <div className="space-y-3 text-sm text-[#A8A090]" data-testid="chapter-widget">
      <div className="flex flex-wrap items-center justify-between gap-2">{backLink}
        <button onClick={() => panelApi.current?.removeChapter()} className="flex items-center gap-1 text-xs text-[#F59E0B] hover:underline" data-testid="chapter-widget-to-top"><PanelTop className="h-3.5 w-3.5" />Move to top</button>
      </div>
      <ChapterNav chapters={ch.chapters} cid={cid} link={link} />
      <div className="space-y-2">{numberEl}<div className="[&_input]:max-w-none">{titleEl}</div></div>
      {actionsEl && <div className="flex flex-wrap items-center gap-2">{actionsEl}</div>}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">{statsEls}</div>
    </div>
  );
  return (
    <DesktopOnly>
      <div className="flex gap-6 px-6 py-6" data-testid="chapter-page">
      <div className="min-w-0 flex-1">
        {!inPanel && (
          <div ref={headRef} className="sticky top-0 z-30 -mx-6 -mt-6 mb-4 border-b border-[#2D3442]/70 bg-[#0B0D10]/95 px-6 pb-3 pt-4 backdrop-blur-md" data-testid="chapter-header">
            {headCollapsed ? (
              <div className="mx-auto flex max-w-5xl items-center gap-3 text-sm text-[#A8A090]" data-testid="chapter-header-collapsed">
                <span className="whitespace-nowrap font-display text-xl text-[#F59E0B]">Chapter {chapterNo}</span>
                {title && <span className="min-w-0 truncate font-display text-xl text-[#F3EBDD]">· {title}</span>}
                <span className="ml-auto whitespace-nowrap"><b className="text-[#F3EBDD]">{fmtNum(words)}</b> words</span>
                {editable && <SaveState state={state} />}
                {headControls}
              </div>
            ) : (
              <>
                <div className="mx-auto mb-3 flex max-w-5xl items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">{backLink}</div>
                  <div className="flex flex-wrap items-center gap-2">{actionsEl}<ChapterNav chapters={ch.chapters} cid={cid} link={link} />{headControls}</div>
                </div>
                <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-3">
                  <div className="flex min-w-[320px] flex-1 items-center gap-3">{numberEl}{titleEl}</div>
                  <div className="ml-auto flex flex-wrap items-center gap-4 text-sm text-[#A8A090]">{statsEls}</div>
                </div>
              </>
            )}
          </div>
        )}
        {!editable && <p className="mx-auto mb-4 flex max-w-5xl items-center gap-1.5 text-xs text-[#FBBF24]" data-testid="chapter-readonly-note"><Lock className="h-3.5 w-3.5" />{ch.locked ? `${ch.draft.name} is read-only.` : "Read-only view."}</p>}
        <div>
          <PageEditor stickyTop={inPanel ? 0 : headH} value={content} onChange={(md) => { if (md === content) return; setContent(md); queue({ content: md }); }} disabled={!editable} pageSize={size} onPages={onPages} onEditor={onEditor} extensions={[lc.ext, poetryExt, scriptExt]} extraTools={tools} bubble={bubble} />
        </div>
      </div>
      <PoetryContext.Provider value={{ editor: editorObj, mode, form, setForm, editable }}>
      <WidgetPanel apiRef={panelApi} getSceneText={() => sceneText(editorObj)} chapterTools={chapterTools} chapterInfo={infoEl} onChapterSlot={setInPanel} sid={sid} uid={ch.story.universe_id} onInsert={editable && editorObj ? (t) => { const { from } = editorObj.state.selection; const prev = from > 1 ? editorObj.state.doc.textBetween(from - 1, from) : ""; const lead = t.startsWith("<p>") || !prev || /\s/.test(prev) ? "" : " "; editorObj.chain().focus().insertContent(lead + t).run(); } : null} />
      </PoetryContext.Provider>
      </div>
      {lc.issue && <IssuePopover issue={lc.issue} onApply={lc.apply} onIgnore={lc.ignore} onLearn={lc.learn} onClose={lc.closeIssue} />}
      <ReviewSheet loreOnly aiChecks={ch.ai_checks} info={info} onOpenInfo={() => { setReviewOpen(false); setInfoOpen(true); }} open={reviewOpen} onOpenChange={setReviewOpen} cid={cid} fixes={fixes} setFixes={setFixes} suggestions={suggestions} setSuggestions={setSuggestions}
        onApply={applyFix} onSendToNotes={(sg) => nav(`${base}/notes`, { state: { noteTopic: sg.subject } })} />
      {annot && <AnnotationDialog uid={ch.story.universe_id} kind={annot} onClose={() => setAnnot(null)} onInsert={(t) => insertAnnotation(editorObj, annot, t)} />}
      {infoSheet}
      {daily.party && <Confetti />}
      {shareText && <ShareQuoteDialog quote={{ id: "selection", lines: [{ text: shareText, speaker_name: "" }] }} uid={ch.story.universe_id} defaultStoryId={sid} editableSpeaker onClose={() => setShareText(null)} />}
    </DesktopOnly>
  );
}
